import { useState, useCallback, useEffect } from 'react';

const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().split('T')[0];
};

/**
 * Shared state + fetch logic for every log page.
 * @param {Function} fetchFn  – LogService.xxx
 */
const useLogPage = (fetchFn) => {
  const [rows,       setRows]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [trend,      setTrend]      = useState([]);
  const [donut,      setDonut]      = useState([]);
  const [stats,      setStats]      = useState({});

  const [filters, setFilters] = useState({
    search:   '',
    status:   '',
    dateFrom: monthStart(),
    dateTo:   today(),
    pageSize: 10,
  });

  // Pending filters (applied only on "Apply" click)
  const [pendingFilters, setPendingFilters] = useState({ ...filters });

  const load = useCallback(async (currentPage, currentFilters) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page:      currentPage,
        page_size: currentFilters.pageSize,
        ...(currentFilters.search   ? { search:    currentFilters.search   } : {}),
        ...(currentFilters.status   ? { status:    currentFilters.status   } : {}),
        ...(currentFilters.dateFrom ? { date_from: currentFilters.dateFrom } : {}),
        ...(currentFilters.dateTo   ? { date_to:   currentFilters.dateTo   } : {}),
      };
      const data = await fetchFn(params);

      // Normalise various API response shapes
      const records     = data?.results ?? data?.data ?? data?.logs ?? data?.items ?? [];
      const total       = data?.count   ?? data?.total ?? data?.total_count ?? records.length;
      const pages       = data?.total_pages ?? (Math.ceil(total / currentFilters.pageSize) || 1);

      // Ensure each record has a stable `id` for table keys
      const normalized = Array.isArray(records) ? records.map(r => ({ id: r.id ?? r.log_id ?? r.logId ?? undefined, ...r })) : [];
      setRows(normalized);
      setTotalItems(total);
      setTotalPages(pages);
      setTrend(Array.isArray(data?.trend) ? data.trend : []);
      setDonut(Array.isArray(data?.donut) ? data.donut : []);
      setStats(data?.stats && typeof data.stats === 'object' ? data.stats : {});
    } catch (err) {
      setError(err.message || 'Failed to load logs');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  // Initial load
  useEffect(() => { load(1, filters); }, []); // eslint-disable-line

  const handleFilterChange = (key, value) => {
    setPendingFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    load(1, pendingFilters);
  };

  const handleReset = () => {
    const def = { search: '', status: '', dateFrom: monthStart(), dateTo: today(), pageSize: 10 };
    setPendingFilters(def);
    setFilters(def);
    setPage(1);
    load(1, def);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    load(newPage, filters);
  };

  const handleRefresh = () => load(page, filters);

  return {
    rows, loading, error,
    page, totalPages, totalItems,
    trend, donut, stats,
    filters: pendingFilters,
    handleFilterChange, handleApply, handleReset,
    handlePageChange, handleRefresh,
  };
};

export default useLogPage;
